import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { IOption, ThemeName } from "@evergis/ui";
import { IconTypesKeys } from "@evergis/ui/dist/atoms/Icon";

import { ADMIN_ROLE } from "../../../utils/constants";
import { useToggle } from "../../../hooks/useToggle";
import { usePanelSelectHandlers } from "./usePanelSelectHandlers";
import { resetMouseLabel } from "../../../ducks/mouseLabel/mouseLabel";
import { getThemeName } from "../../../ducks/theme/selectors";
import { getAppConfig } from "../../../ducks/appConfig";
import { changeThemeName } from "../../../ducks/theme/theme";
import { PanelConfig } from "./usePanelConfig";

type ProjectPanelOption = IOption & {
  enabled: (config: PanelConfig) => boolean;
};

// eslint-disable-next-line max-lines-per-function
export const useProjectMenu = (config: PanelConfig) => {
  const { t } = useTranslation("projectPanel");
  const dispatch = useDispatch();
  const { settings } = useSelector(getAppConfig);
  const themeName = useSelector(getThemeName);
  const [isProjectMenuOpen, toggleProjectMenu] = useToggle(false);
  const handlers = usePanelSelectHandlers();

  const options = useMemo(
    // eslint-disable-next-line max-lines-per-function
    () => [
      {
        text: t("newMap"),
        value: "newMap",
        icon: "layers" as IconTypesKeys,
        enabled: ({ shared }) => !shared,
      },
      {
        text: t("openMap"),
        value: "open-another",
        icon: "map" as IconTypesKeys,
        enabled: ({ shared }) => !shared,
      },
      {
        text: t("projectProperties"),
        value: "projectProperties",
        icon: "settings" as IconTypesKeys,
        enabled: ({ shared, configurable, isOwner, isTempProject, userRoles }) =>
          !shared && !isTempProject && (configurable || isOwner || userRoles?.includes(ADMIN_ROLE)),
      },
      {
        text: t("actions.save", { ns: "common" }),
        icon: "save" as IconTypesKeys,
        value: "save-temp-project",
        enabled: ({ shared, hasLayers, isTempProject }) => !shared && isTempProject && hasLayers,
      },
      {
        text: t("accessSettings", { ns: "common" }),
        value: "accessSettings",
        icon: "key" as IconTypesKeys,
        enabled: ({ shared, isOwner, configurable, userRoles }) =>
          !shared && (configurable || isOwner || userRoles?.includes(ADMIN_ROLE)),
      },
      {
        text: t("copyMap"),
        value: "save-as-new",
        icon: "copy" as IconTypesKeys,
        enabled: ({ shared, isTempProject, hasLayers }) => !shared && !isTempProject && hasLayers,
      },
      /* {
          text: "Добавить в избранное",
          value: "favorite-change",
          enabled: config => config.shared && !config.isFavorite,
        },
        {
          text: "Удалить из избранного",
          value: "favorite-change",
          enabled: config => config.shared && config.isFavorite,
        },*/
      {
        text: t("actions.share", { ns: "common" }),
        icon: "share" as IconTypesKeys,
        value: "share",
        enabled: ({ shared, isTempProject }) => !shared && !isTempProject,
      },
      {
        text: t("exportImage"),
        value: "print",
        icon: "image" as IconTypesKeys,
        enabled: ({ printEnabled }) => printEnabled,
      },
      {
        text: t("copyWmsLink"),
        value: "copy-wms",
        icon: "wms_link" as IconTypesKeys,
        enabled: ({ isTempProject }) => !isTempProject,
      },
      {
        text: t("configurations"),
        value: "configurations",
        icon: "configurations" as IconTypesKeys,
        enabled: ({ configurable, userRoles, isOwner, isTempProject }) =>
          !isTempProject && (configurable || isOwner || userRoles?.includes(ADMIN_ROLE)),
      },
      {
        text: t("deleteMap"),
        value: "delete",
        icon: "delete" as IconTypesKeys,
        enabled: ({ userRoles, shared, isTempProject, isOwner }) =>
          !shared && !isTempProject && (isOwner || userRoles?.includes(ADMIN_ROLE)),
      },
    ],
    [t],
  );

  const projectOptions = useMemo(() => options.filter(option => option.enabled(config)), [config, options]);

  const selectProjectOption = useCallback(
    ([{ value }]: ProjectPanelOption[]) => {
      if (handlers[value]) {
        dispatch(resetMouseLabel());

        handlers[value]();
      }
      toggleProjectMenu();
    },
    [dispatch, handlers, toggleProjectMenu],
  );

  const toggleTheme = useCallback(() => {
    dispatch(changeThemeName(themeName === ThemeName.Dark ? (settings?.theme?.name as ThemeName) : ThemeName.Dark));
  }, [dispatch, settings?.theme?.name, themeName]);

  return {
    isProjectMenuOpen,
    toggleProjectMenu,
    toggleTheme,
    projectOptions,
    selectProjectOption,
  };
};
